import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Analytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [mrsList, setMrsList] = useState([]);
  const [selectedMRS, setSelectedMRS] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
    fetchMRSList();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard?user_id=${user.id}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMRSList = async () => {
    try {
      const response = await axios.get(`${API}/mrs?user_id=${user.id}`);
      setMrsList(response.data);
    } catch (error) {
      console.error('Error fetching MRS list:', error);
    }
  };

  const generateReport = () => {
    // This would generate a publication-ready report
    alert('Generating comprehensive research report... This feature will export your data analysis in a publication-ready format.');
  };

  const exportData = () => {
    // This would export the selected dataset
    alert('Exporting dataset... This feature will allow you to download your data in various formats (CSV, Excel, SPSS).');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Research & Analytics</h1>
          <p className="text-gray-600 mt-2">Generate insights and publication-ready reports from your medical data</p>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={exportData}
            className="btn-secondary flex items-center space-x-2"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            <span>Export Data</span>
          </button>
          <button
            onClick={generateReport}
            className="btn-primary flex items-center space-x-2"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
              <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
            </svg>
            <span>Generate Report</span>
          </button>
        </div>
      </div>

      {/* Dataset Selector */}
      <div className="medical-card">
        <div className="medical-card-content">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Analyze Dataset:</label>
            <select
              value={selectedMRS}
              onChange={(e) => setSelectedMRS(e.target.value)}
              className="form-input py-2 px-3 w-64"
            >
              <option value="">All Datasets</option>
              {mrsList.map(mrs => (
                <option key={mrs.id} value={mrs.id}>
                  {mrs.name} ({mrs.patient_count} patients)
                </option>
              ))}
            </select>
            <button className="btn-primary text-sm">
              Analyze Selected Dataset
            </button>
            {selectedMRS && (
              <Link
                to={`/mrs/${selectedMRS}/analytics-chat`}
                className="btn-primary text-sm flex items-center space-x-2 bg-sky-600 hover:bg-sky-700"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                </svg>
                <span>AI Chat Analysis</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="stats-card bg-gradient-to-r from-sky-500 to-sky-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold">{analytics?.mrs_count || 0}</div>
              <div className="text-sky-100">Medical Record Sets</div>
            </div>
            <svg className="h-8 w-8 text-sky-200" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
            </svg>
          </div>
        </div>

        <div className="stats-card bg-gradient-to-r from-green-500 to-green-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold">{analytics?.total_patients || 0}</div>
              <div className="text-green-100">Total Patients</div>
            </div>
            <svg className="h-8 w-8 text-green-200" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
            </svg>
          </div>
        </div>

        <div className="stats-card bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold">{Object.keys(analytics?.specialty_distribution || {}).length}</div>
              <div className="text-purple-100">Active Specialties</div>
            </div>
            <svg className="h-8 w-8 text-purple-200" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
            </svg>
          </div>
        </div>

        <div className="stats-card bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold">{analytics?.recent_conversations || 0}</div>
              <div className="text-orange-100">AI Analyses</div>
            </div>
            <svg className="h-8 w-8 text-orange-200" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>

      {/* Specialty Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="medical-card">
          <div className="medical-card-header">
            <h3 className="text-lg font-semibold text-gray-900">Specialty Distribution</h3>
          </div>
          <div className="medical-card-content">
            {analytics?.specialty_distribution && Object.keys(analytics.specialty_distribution).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(analytics.specialty_distribution).map(([specialty, count]) => (
                  <div key={specialty} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-4 h-4 rounded-full specialty-color-${specialty.toLowerCase()}`}></div>
                      <span className="text-gray-900 font-medium">{specialty}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600">{count} MRS</span>
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-sky-600 h-2 rounded-full"
                          style={{ width: `${(count / Math.max(...Object.values(analytics.specialty_distribution))) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <svg className="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p>No data available for visualization</p>
              </div>
            )}
          </div>
        </div>

        <div className="medical-card">
          <div className="medical-card-header">
            <h3 className="text-lg font-semibold text-gray-900">Research Capabilities</h3>
          </div>
          <div className="medical-card-content">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-sky-100 rounded-lg">
                  <svg className="h-5 w-5 text-sky-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Clinical Outcomes Analysis</h4>
                  <p className="text-sm text-gray-600">Analyze therapy success rates and patient outcomes</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Adverse Event Detection</h4>
                  <p className="text-sm text-gray-600">Identify safety signals and risk patterns</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                    <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Statistical Analysis</h4>
                  <p className="text-sm text-gray-600">Perform cohort comparisons and trend analysis</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg className="h-5 w-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Publication Reports</h4>
                  <p className="text-sm text-gray-600">Generate publication-ready research summaries</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Analysis Tools */}
      <div className="medical-card">
        <div className="medical-card-header">
          <h3 className="text-lg font-semibold text-gray-900">Quick Analysis Tools</h3>
        </div>
        <div className="medical-card-content">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button className="p-4 bg-sky-50 hover:bg-sky-100 rounded-lg text-left transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-sky-100 rounded-lg">
                  <svg className="h-5 w-5 text-sky-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Cohort Analysis</h4>
                  <p className="text-xs text-gray-600">Compare patient groups</p>
                </div>
              </div>
            </button>

            <button className="p-4 bg-green-50 hover:bg-green-100 rounded-lg text-left transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Trend Analysis</h4>
                  <p className="text-xs text-gray-600">Identify patterns over time</p>
                </div>
              </div>
            </button>

            <button className="p-4 bg-red-50 hover:bg-red-100 rounded-lg text-left transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Safety Analysis</h4>
                  <p className="text-xs text-gray-600">Adverse event patterns</p>
                </div>
              </div>
            </button>

            <button className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg text-left transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg className="h-5 w-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Efficacy Review</h4>
                  <p className="text-xs text-gray-600">Treatment success rates</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* AI-Powered Insights */}
      <div className="medical-card bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <div className="medical-card-content">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <svg className="h-6 w-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-indigo-900 mb-2">AI-Powered Research Insights</h3>
              <div className="space-y-2 text-sm text-indigo-800">
                <div className="flex items-center space-x-2">
                  <svg className="h-4 w-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Use our AI Assistant to ask specific clinical questions about your data</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="h-4 w-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Generate automated research summaries and statistical analyses</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="h-4 w-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Compare your results against clinical guidelines and published studies</span>
                </div>
              </div>
              <div className="mt-4">
                <a 
                  href="/ai-chat"
                  className="inline-flex items-center space-x-2 text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  <span>Start AI Analysis</span>
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;